import { type ReactNode, useEffect } from 'react';
import { Link } from 'react-router-dom';

const UPDATED = 'July 21, 2026';
const SERVICE = 'KatSquare Draft';
const ENTITY = 'KatSquare';
const CONTACT = 'support@katsquare.cloud';
const ADDRESS = 'Garuda Complex, MP Prakash Nagar, Hosapete, Karnataka 583201, India';
const JURISDICTION = 'Hosapete, Karnataka, India';

/** Shared shell for the public legal pages (privacy, terms). */
function LegalShell({ title, children }: { title: string; children: ReactNode }) {
  // Arriving from the landing-page footer carries its scroll position; reset it.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [title]);
  return (
    <div className="min-h-full bg-white text-neutral-800">
      <header className="border-b border-neutral-200">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-4">
          <Link to="/" className="font-bold tracking-tight text-neutral-900">
            {SERVICE}
          </Link>
          <nav className="ml-auto flex items-center gap-4 text-sm text-neutral-500">
            <Link to="/terms" className="hover:text-neutral-900">
              Terms
            </Link>
            <Link to="/privacy" className="hover:text-neutral-900">
              Privacy
            </Link>
            <Link to="/login" className="hover:text-neutral-900">
              Sign in
            </Link>
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-6 py-10">
        <h1 className="text-3xl font-bold tracking-tight text-neutral-900">{title}</h1>
        <p className="mt-1 text-sm text-neutral-500">Last updated: {UPDATED}</p>
        <div className="mt-8 space-y-6 text-[15px] leading-relaxed">{children}</div>
        <footer className="mt-12 border-t border-neutral-200 pt-6 text-sm text-neutral-500">
          <p>
            {ENTITY} · {ADDRESS} · {CONTACT}
          </p>
          <p className="mt-2">
            <Link to="/terms" className="text-blue-600 hover:text-blue-700">
              Terms of Service
            </Link>{' '}
            ·{' '}
            <Link to="/privacy" className="text-blue-600 hover:text-blue-700">
              Privacy Policy
            </Link>
          </p>
        </footer>
      </main>
    </div>
  );
}

function H({ children }: { children: ReactNode }) {
  return <h2 className="mt-8 text-lg font-semibold text-neutral-900">{children}</h2>;
}
function P({ children }: { children: ReactNode }) {
  return <p className="text-neutral-700">{children}</p>;
}
function UL({ children }: { children: ReactNode }) {
  return <ul className="list-disc space-y-1 pl-6 text-neutral-700">{children}</ul>;
}

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy">
      <P>
        This Privacy Policy explains how {ENTITY} (“we”, “us”, “our”), the operator of {SERVICE} (the
        “Service”), collects, uses, discloses, and safeguards your information when you use our website
        and applications. By using the Service you agree to the practices described here. If you do not
        agree, please do not use the Service.
      </P>

      <H>1. Information We Collect</H>
      <P>We collect information in the following categories:</P>
      <UL>
        <li>
          <strong>Account information</strong> — your name, email address, and password (stored only as a
          salted hash), and any profile details you provide.
        </li>
        <li>
          <strong>Content you upload</strong> — the PDF proofs, images, comments, annotations, and other
          material you or your team add to a workspace. This content is stored to provide the Service.
        </li>
        <li>
          <strong>Billing information</strong> — when you subscribe to a paid plan, payments are processed
          by our third-party payment processor (Razorpay). We do not store your full card details on our
          servers; we retain only limited transaction metadata (such as plan, amount, status, and an
          order/payment reference) needed to manage your subscription.
        </li>
        <li>
          <strong>Usage &amp; device data</strong> — log data such as IP address, browser/user-agent,
          timestamps, and actions taken within the Service, used for security, debugging, and analytics.
        </li>
        <li>
          <strong>Cookies</strong> — a strictly necessary session cookie to keep you signed in. We do not
          use third-party advertising cookies.
        </li>
      </UL>

      <H>2. How We Use Your Information</H>
      <UL>
        <li>To provide, operate, maintain, and improve the Service;</li>
        <li>To authenticate you and secure your account;</li>
        <li>To process subscriptions, payments, invoices, and renewals;</li>
        <li>To send transactional and notification emails (e.g. review activity, invitations);</li>
        <li>To respond to support requests and communicate about the Service;</li>
        <li>To detect, prevent, and address fraud, abuse, and technical issues;</li>
        <li>To comply with legal obligations.</li>
      </UL>

      <H>3. How We Share Information</H>
      <P>
        We do not sell your personal information. We share it only with service providers who process it on
        our behalf under appropriate confidentiality obligations, including:
      </P>
      <UL>
        <li>
          <strong>Razorpay</strong> — payment processing. Your payment is subject to Razorpay’s own privacy
          policy.
        </li>
        <li>
          <strong>Email delivery</strong> — a transactional email provider used to send notifications.
        </li>
        <li>
          <strong>Hosting &amp; storage</strong> — cloud infrastructure and object-storage providers that
          host the Service and your content.
        </li>
      </UL>
      <P>
        We may also disclose information if required by law, to enforce our Terms, or to protect the rights,
        property, or safety of our users or others.
      </P>

      <H>4. Data Retention</H>
      <P>
        We retain your account and content for as long as your account is active. Deleted items may be kept
        in a recycle bin for a limited period before permanent deletion. When you close your account, we
        delete or anonymize your personal data within a reasonable period, except where retention is
        required for legal, tax, accounting, or fraud-prevention purposes.
      </P>

      <H>5. Security</H>
      <P>
        We use industry-standard measures to protect your data, including encryption in transit (HTTPS),
        hashed passwords, access controls, and isolated storage. No method of transmission or storage is
        completely secure, and we cannot guarantee absolute security.
      </P>

      <H>6. Your Rights</H>
      <P>
        Depending on your location, you may have the right to access, correct, export, or delete your
        personal data, and to object to or restrict certain processing. You can update your profile in the
        app, or contact us at {CONTACT} to exercise these rights. We will respond within the timeframe
        required by applicable law.
      </P>

      <H>7. International Transfers</H>
      <P>
        Your information may be processed in countries other than your own. Where required, we take steps to
        ensure an adequate level of protection for your data.
      </P>

      <H>8. Children</H>
      <P>
        The Service is not directed to children under 16, and we do not knowingly collect their personal
        information. If you believe a child has provided us data, please contact us and we will delete it.
      </P>

      <H>9. Changes to This Policy</H>
      <P>
        We may update this Policy from time to time. We will post the revised version here and update the
        “Last updated” date. Material changes may be notified by email or in-app.
      </P>

      <H>10. Contact Us</H>
      <P>
        Questions about this Policy or your data? Contact {ENTITY} at {CONTACT}, {ADDRESS}.
      </P>
    </LegalShell>
  );
}

export function TermsPage() {
  return (
    <LegalShell title="Terms of Service">
      <P>
        These Terms of Service (“Terms”) govern your access to and use of {SERVICE} (the “Service”),
        operated by {ENTITY} (“we”, “us”, “our”). By creating an account or using the Service, you agree to
        these Terms. If you are using the Service on behalf of an organization, you represent that you have
        authority to bind that organization.
      </P>

      <H>1. The Service</H>
      <P>
        {SERVICE} is a collaborative proofing platform for reviewing PDF documents, leaving comments and
        annotations, comparing versions, and managing approvals across a team.
      </P>

      <H>2. Accounts &amp; Eligibility</H>
      <P>
        You must provide accurate information, keep your credentials confidential, and are responsible for
        all activity under your account. You must be at least 18 years old, or the age of majority in your
        jurisdiction, to create a paid account. Notify us immediately of any unauthorized use.
      </P>

      <H>3. Acceptable Use</H>
      <P>You agree not to:</P>
      <UL>
        <li>Upload content you do not have the right to use, or that infringes others’ rights;</li>
        <li>Upload unlawful, harmful, or malicious material;</li>
        <li>Attempt to breach security, reverse engineer, or disrupt the Service;</li>
        <li>Use the Service to send spam or violate applicable laws;</li>
        <li>Resell or provide the Service to third parties except as permitted by your plan.</li>
      </UL>

      <H>4. Your Content</H>
      <P>
        You retain all ownership of the documents and content you upload. You grant us a limited license to
        host, store, process, and display that content solely to operate and provide the Service to you and
        the collaborators you invite. You are responsible for your content and for having the necessary
        rights and consents.
      </P>

      <H>5. Plans, Subscriptions &amp; Billing</H>
      <UL>
        <li>
          The Service offers a free plan and paid subscription plans (e.g. Pro and Business) with the
          features and limits described on our pricing page.
        </li>
        <li>
          Paid subscriptions are billed in advance on a recurring basis (monthly, per the plan you select)
          via our payment processor, <strong>Razorpay</strong>. Prices are shown at checkout and are
          exclusive of taxes unless stated otherwise.
        </li>
        <li>
          By subscribing, you authorize us and Razorpay to charge your chosen payment method on each renewal
          until you cancel.
        </li>
        <li>
          We may change plan pricing or features with reasonable prior notice; changes take effect at your
          next renewal.
        </li>
      </UL>

      <H>6. Cancellation &amp; Refunds</H>
      <UL>
        <li>
          You may cancel your subscription at any time from your account settings or by contacting{' '}
          {CONTACT}. Cancellation stops future renewals.
        </li>
        <li>
          Unless required by law or expressly stated otherwise, payments are non-refundable and there are no
          refunds or credits for partial billing periods, unused time, or unused features. On cancellation,
          you retain paid features until the end of the current billing period, after which your account
          reverts to the free plan.
        </li>
        <li>
          If you believe you were billed in error, contact us at {CONTACT} within 7 days and we will review
          the charge in good faith.
        </li>
      </UL>

      <H>7. Plan Limits</H>
      <P>
        Each plan has usage limits (such as storage, number of documents, and the number of manager and
        reviewer seats). We may enforce these limits by preventing actions that would exceed them. Exceeding
        limits may require upgrading your plan.
      </P>

      <H>8. Termination</H>
      <P>
        You may stop using the Service and delete your account at any time. We may suspend or terminate your
        access if you breach these Terms, fail to pay, or use the Service in a way that risks harm to us or
        others. On termination, your right to use the Service ends and we may delete your content in
        accordance with our Privacy Policy.
      </P>

      <H>9. Disclaimers</H>
      <P>
        The Service is provided “as is” and “as available”, without warranties of any kind, whether express
        or implied, including fitness for a particular purpose, merchantability, and non-infringement. We do
        not warrant that the Service will be uninterrupted, error-free, or secure.
      </P>

      <H>10. Limitation of Liability</H>
      <P>
        To the maximum extent permitted by law, {ENTITY} will not be liable for any indirect, incidental,
        special, consequential, or punitive damages, or any loss of data, revenue, or profits. Our total
        liability for any claim arising out of or relating to the Service will not exceed the amount you paid
        us in the 12 months preceding the claim.
      </P>

      <H>11. Indemnification</H>
      <P>
        You agree to indemnify and hold harmless {ENTITY} from any claims, damages, or expenses arising from
        your content, your use of the Service, or your violation of these Terms.
      </P>

      <H>12. Changes to These Terms</H>
      <P>
        We may update these Terms from time to time. We will post the revised version here and update the
        “Last updated” date. Continued use of the Service after changes take effect constitutes acceptance.
      </P>

      <H>13. Governing Law</H>
      <P>
        These Terms are governed by the laws of {JURISDICTION}, without regard to conflict-of-laws
        principles. The courts located in {JURISDICTION} will have exclusive jurisdiction over any disputes,
        subject to applicable law.
      </P>

      <H>14. Contact</H>
      <P>
        Questions about these Terms? Contact {ENTITY} at {CONTACT}, {ADDRESS}.
      </P>
    </LegalShell>
  );
}
